import {
  FC,
  PersistenceProvider,
  useCreateDebouncedPersistence,
} from "@orgiggly/shared";
import { lazy, Suspense, useEffect } from "react";
import { ParamsProvider, useCreateParams, useParams } from "./apps/rootHooks";
import { LoadingSplash } from "./components/app/LoadingSplash";

const Admin = lazy(() => import("./apps/admin/App"));
const Site = lazy(() => import("./apps/site/SiteApp"));
const Terms = lazy(() => import("./apps/site/screens/Terms"));
const Privacy = lazy(() => import("./apps/site/screens/Privacy"));
const Demo = lazy(() => import("./apps/demo/DemoApp"));
const Embed = lazy(() => import("./apps/embed/EmbedApp"));
const ConnectSpotify = lazy(() => import("./apps/connect/ConnectSpotify"));
const ConnectCli = lazy(() => import("./apps/connect/ConnectCli"));
const Create = lazy(() => import("./apps/create/CreateApp"));
const DocsIndex = lazy(() => import("./apps/docsIndex/DocsIndex"));
import { defaultLocalStoragePersistence } from "./utils/localStoragePersistence";
import { SnackBarWrapper } from "./components/snackbar/SnackBarWrapper";
import { PwaUpdatePrompt } from "./components/app/PwaUpdatePrompt";
import { RouteErrorBoundary } from "./components/app/RouteErrorBoundary";
import { initAnalytics } from "./utils/analytics";

// Static URL routes handled above the admin/site dispatch. Site routing
// otherwise treats single-segment paths as namespace lookups, so /terms,
// /privacy, /demo etc. must be intercepted here to keep them out of the org
// URL space. A new route's first segment also belongs in RESERVED_USERNAMES
// (firebase-admin `orgIdUtils.ts`), or a user can claim it as a namespace.
const STATIC_ROUTES: Record<string, FC> = {
  "/terms": Terms,
  "/privacy": Privacy,
  "/demo": Demo,
  "/connect/spotify": ConnectSpotify,
  "/connect/cli": ConnectCli,
  "/create": Create,
  // The hosted-doc index (`og doc publish`); /d/<name> itself is api/doc.ts.
  "/d": DocsIndex,
  "/d/": DocsIndex,
};

const Root: FC = () => {
  return (
    <ParamsProvider value={useCreateParams()}>
      <SnackBarWrapper>
        <PwaUpdatePrompt />
        <DataWrapper>
          <RouteErrorBoundary>
            <Internal />
          </RouteErrorBoundary>
        </DataWrapper>
      </SnackBarWrapper>
    </ParamsProvider>
  );
};

const Internal: FC = () => {
  const { isAdmin, embedMode } = useParams();
  useEffect(() => {
    document.title = isAdmin ? "Orgiggly Admin" : "Orgiggly";
  }, [isAdmin]);
  useEffect(() => {
    initAnalytics();
  }, []);
  const Static = STATIC_ROUTES[window.location.pathname];
  if (Static) {
    return (
      <Suspense fallback={<LoadingSplash stage="code" />}>
        <Static />
      </Suspense>
    );
  }
  if (embedMode) {
    return (
      <Suspense fallback={<LoadingSplash stage="code" />}>
        <Embed />
      </Suspense>
    );
  }
  return (
    <Suspense fallback={<LoadingSplash stage="code" />}>
      {isAdmin ? <Admin /> : <Site />}
    </Suspense>
  );
};

export default Root;

const DataWrapper: FC = ({ children }) => {
  const debouncedPersistence = useCreateDebouncedPersistence(
    defaultLocalStoragePersistence
  );
  return (
    <PersistenceProvider value={debouncedPersistence}>
      {children}
    </PersistenceProvider>
  );
};
